import { useToast, useAppVariant, useTranslations } from "@jugl-web/utils";
import {
  useRestApiProvider,
  type ConnectionDto,
  type ConnectionPropsBase,
  type ConnectionType,
} from "@jugl-web/rest-api";
import { useShowAlert } from "@jugl-web/domain-resources/ui-components";
import { useEntitySelectedProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntitySelectedProvider";
import { environment } from "@web-src/environments/environment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CXIntegrationTileProps,
  CXIntegrationTile,
} from "./components/CXIntegrationTile";
import { ReactComponent as SquareIcon } from "./assets/square.svg";
import { ReactComponent as ShopifyIcon } from "./assets/shopify.svg";
import { ReactComponent as CalendlyIcon } from "./assets/calendly.svg";
import { ConnectionPickerDialog } from "./components/ConnectionPickerDialog";

const SHOPIFY_CLIENT_ID = "e9f55dc12c7f745c525a0e80d95854bc";

type OAuthProvider = "calendly" | "squareup";

type PickerState = {
  provider: OAuthProvider;
  connectionId: string;
};

type PendingConnectionStep = {
  provider: OAuthProvider;
  connectionId: string;
};

const buildOAuthUrl = (
  provider: OAuthProvider,
  { entityId, name }: { entityId: string; name: string }
) => {
  const finalCallbackUrl = `${window.location.origin}/connections/${provider}`;
  const params = new URLSearchParams({
    entity_id: entityId,
    name,
    final_callback_url: finalCallbackUrl,
  });
  return `${
    environment.apiUrl
  }/api/connections/${provider}/authorize?${params.toString()}`;
};

type TileState = {
  isConnected: boolean;
  needsSetup: boolean;
  connectionId: string | null;
};

const deriveTileState = (
  connections: ConnectionDto[] | undefined
): TileState => {
  const first = connections?.[0];
  if (!first) {
    return { isConnected: false, needsSetup: false, connectionId: null };
  }
  const status = (first.props as ConnectionPropsBase | undefined)?.status;
  if (status === "connected") {
    return { isConnected: true, needsSetup: false, connectionId: first.id };
  }
  return { isConnected: true, needsSetup: true, connectionId: first.id };
};

export const CXIntegrationsList: React.FC = () => {
  const { t } = useTranslations();
  const { variant } = useAppVariant();
  const { toast } = useToast({ variant });
  const showAlert = useShowAlert();
  const { entityId } = useEntitySelectedProvider();
  const { integrationsApi } = useRestApiProvider();
  const [deleteConnection] = integrationsApi.useDeleteConnectionMutation();
  const location = useLocation();
  const navigate = useNavigate();

  const { data: allConnections } = integrationsApi.useGetConnectionsQuery({
    entityId,
  });

  const connectionsByType = useMemo(
    () =>
      (allConnections?.data ?? []).reduce((acc, connection) => {
        const group = acc[connection.type] ?? [];
        group.push(connection);
        acc[connection.type] = group;
        return acc;
      }, {} as Partial<Record<ConnectionType, ConnectionDto[]>>),
    [allConnections]
  );

  const calendlyTile = useMemo(
    () => deriveTileState(connectionsByType.calendly),
    [connectionsByType]
  );
  const squareupTile = useMemo(
    () => deriveTileState(connectionsByType.squareup),
    [connectionsByType]
  );
  const isShopifyConnected = Boolean(connectionsByType.shopify?.length);

  const [picker, setPicker] = useState<PickerState | null>(null);

  useEffect(() => {
    const state = location.state as {
      pendingConnectionStep?: PendingConnectionStep;
    } | null;
    if (state?.pendingConnectionStep) {
      setPicker(state.pendingConnectionStep);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location.state, location.pathname, navigate]);

  const startOAuth = useCallback(
    (provider: OAuthProvider, name: string) => {
      window.location.href = buildOAuthUrl(provider, { entityId, name });
    },
    [entityId]
  );

  const openPicker = useCallback(
    (provider: OAuthProvider, connectionId: string | null) => {
      if (!connectionId) return;
      setPicker({ provider, connectionId });
    },
    []
  );

  const handleDisconnect = useCallback(
    (integrationName: string, connectionId: string | null) => {
      if (!connectionId) return;
      showAlert({
        title: t(
          {
            id: "cx-actions-page.disconnect-title",
            defaultMessage: "Disconnect {integration}?",
          },
          { integration: integrationName }
        ),
        content: t({
          id: "cx-actions-page.disconnect-content",
          defaultMessage:
            "This will remove the connection. Any agent tools using this connection will stop working.",
        }),
        buttons: [
          {
            text: t({ id: "common.cancel", defaultMessage: "Cancel" }),
            color: "grey",
            role: "close",
          },
          {
            text: t({
              id: "cx-actions-page.disconnect-confirm",
              defaultMessage: "Disconnect",
            }),
            color: "tertiary",
            onClick: async (_, { closeAlert }) => {
              closeAlert();
              try {
                await deleteConnection({ entityId, connectionId }).unwrap();
                toast(
                  t({
                    id: "cx-actions-page.disconnect-success",
                    defaultMessage: "Disconnected successfully",
                  })
                );
              } catch {
                toast(
                  t({
                    id: "cx-actions-page.disconnect-error",
                    defaultMessage: "Failed to disconnect",
                  })
                );
              }
            },
          },
        ],
      });
    },
    [showAlert, deleteConnection, entityId, toast, t]
  );

  const pendingLabel = t({
    id: "cx-actions-page.finish-setup",
    defaultMessage: "Finish setup",
  });
  const pendingTooltip = t({
    id: "cx-actions-page.connection-requires-configuration",
    defaultMessage:
      "Connection created but not yet configured. Finish setup to enable Actions.",
  });

  const integrations: (CXIntegrationTileProps & { id: string })[] =
    useMemo(() => {
      const baseTile = {
        isConnecting: false,
        isReconnecting: false,
      };

      return [
        {
          ...baseTile,
          id: "calendly",
          title: t({
            id: "cx-actions-page.calendly",
            defaultMessage: "Calendly",
          }),
          description: t({
            id: "cx-actions-page.calendly-description",
            defaultMessage:
              "Let the AI Agent send booking links and help customers schedule calls or consultations without leaving the conversation by using with Calendly",
          }),
          icon: <CalendlyIcon />,
          isConnected: calendlyTile.isConnected,
          needsReconnect: calendlyTile.needsSetup,
          onConnect: () => startOAuth("calendly", "Calendly"),
          onDisconnect: () =>
            handleDisconnect("Calendly", calendlyTile.connectionId),
          onReconnect: () => openPicker("calendly", calendlyTile.connectionId),
          pendingLabel,
          pendingTooltip,
        },
        {
          ...baseTile,
          id: "shopify",
          title: t({
            id: "cx-actions-page.shopify",
            defaultMessage: "Shopify",
          }),
          description: t({
            id: "cx-actions-page.shopify-description",
            defaultMessage:
              "Connect your Shopify store to let the AI Agent answer product questions, check order status, and help customers place orders automatically",
          }),
          icon: <ShopifyIcon />,
          isConnected: isShopifyConnected,
          needsReconnect: false,
          onConnect: () => {
            const url = `https://admin.shopify.com/?organization_id=191918456&no_redirect=true&redirect=/oauth/redirect_from_developer_dashboard?client_id%3D${SHOPIFY_CLIENT_ID}`;
            window.open(url, "_blank");
          },
          onDisconnect: () => {
            const shopifyConn = connectionsByType.shopify?.[0];
            if (shopifyConn) handleDisconnect("Shopify", shopifyConn.id);
          },
          onReconnect: () => {},
        },
        {
          ...baseTile,
          id: "square",
          title: t({
            id: "cx-actions-page.square",
            defaultMessage: "Square",
          }),
          description: t({
            id: "cx-actions-page.square-description",
            defaultMessage:
              "Integrate Square to manage payments and transactions while assisting customers with purchases and order information",
          }),
          icon: <SquareIcon />,
          isConnected: squareupTile.isConnected,
          needsReconnect: squareupTile.needsSetup,
          onConnect: () => startOAuth("squareup", "Squareup"),
          onDisconnect: () =>
            handleDisconnect("Square", squareupTile.connectionId),
          onReconnect: () => openPicker("squareup", squareupTile.connectionId),
          pendingLabel,
          pendingTooltip,
        },
      ];
    }, [
      t,
      pendingLabel,
      pendingTooltip,
      startOAuth,
      openPicker,
      handleDisconnect,
      calendlyTile,
      squareupTile,
      isShopifyConnected,
      connectionsByType,
    ]);

  return (
    <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 p-12">
      <div className="flex flex-col gap-1">
        <span className="font-secondary text-[22px] font-semibold text-[#1A2023]">
          {t({
            id: "cx-actions-page.integrations",
            defaultMessage: "Integrations",
          })}
        </span>
        <span className="text-dark-600 font-secondary text-sm">
          {t({
            id: "cx-actions-page.integrations-with-external-apps",
            defaultMessage:
              "Integrate with external apps to create custom Actions",
          })}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-8">
        {integrations.map((integration) => (
          <CXIntegrationTile key={integration.id} {...integration} />
        ))}
      </div>
      {picker && (
        <ConnectionPickerDialog
          isOpen
          onClose={() => setPicker(null)}
          provider={picker.provider}
          entityId={entityId}
          connectionId={picker.connectionId}
        />
      )}
    </div>
  );
};
